import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Observable, Subject, delay, map, mergeMap, of, retry, tap } from 'rxjs';

import {
    AthenaUsageTier,
    Conversation,
    Message,
    PredefinedQuestion,
    RagFile,
    RagFileStatus,
    SharepointSite,
    TokenCounter,
    UploadedConversationFile,
} from '@/knowledge-center/models';
import { PollingStatus } from '@/tax/models';

import { AuthService } from './auth.service';

import { environment } from '@env';
import { Company } from '@/organisations/models';

type WebSocketParams = {
    webSocket?: WebSocket;
    organisationId?: number;
    conversationId?: number;
    isConnected: boolean;
};

const LONG_DELAY_STATUS_FETCH_MS = 5000;
const invalidErrorMessage = 'Not ready';

@Injectable({
    providedIn: 'root',
})
export class KnowledgeCenterService {
    constructor(
        private http: HttpClient,
        private authService: AuthService,
    ) {}

    private webSocketParams: WebSocketParams = { isConnected: false };
    private message = new Subject<string>();
    private queue: string[] = [];

    listConversations(orgId: number): Observable<Conversation[]> {
        return this.http
            .get<Conversation[]>(environment.endpoints.organisationKnowledgeCenterConversationList(orgId))
            .pipe(map((conversations) => conversations.map((c) => new Conversation().deserialize(c))));
    }

    createConversation(orgId: number, activeAgentModes: string[], companies?: Company[]): Observable<Conversation> {
        const body: any = {
            'active_agent_modes': activeAgentModes,
        };

        if (companies && companies.length > 0) {
            body['company_ids'] = companies.map((c) => c.id);
        }

        return this.http
            .post<Conversation>(environment.endpoints.organisationKnowledgeCenterConversationList(orgId), body)
            .pipe(map((c) => new Conversation().deserialize(c)));
    }

    updateConversation(orgId: number, conversation: Conversation): Observable<Conversation> {
        return this.http
            .put<Conversation>(
                environment.endpoints.organisationKnowledgeCenterConversationDetails(orgId, conversation.id),
                conversation.serialize(),
            )
            .pipe(map((c) => new Conversation().deserialize(c)));
    }

    uploadFileToConversation(orgId: number, file: File, conversationId: number): Observable<UploadedConversationFile> {
        const formData = new FormData();
        formData.append('file', file);

        return this.http
            .post(environment.endpoints.organisationKnowledgeCenterConversationFileList(orgId, conversationId), formData)
            .pipe(map((f) => new UploadedConversationFile().deserialize(f)));
    }

    listMessages(orgId: number, conId: number, limit: number, before?: Date): Observable<Message[]> {
        let paramsContent = {} as Record<string, any>;
        if (before !== undefined) {
            paramsContent['before'] = before;
        }
        return this.http
            .get<Message[]>(environment.endpoints.organisationKnowledgeCenterMessageList(orgId, conId), {
                params: { limit: limit, ...paramsContent },
            })
            .pipe(map((messages) => messages.map((m) => new Message().deserialize(m))));
    }

    connect(orgId: number, conId: number) {
        if (
            this.webSocketParams.webSocket === undefined ||
            this.webSocketParams.organisationId !== orgId ||
            this.webSocketParams.conversationId !== conId
        ) {
            this.message = new Subject<string>();
            this.webSocketParams.webSocket?.close();
            this.webSocketParams = {
                organisationId: orgId,
                conversationId: conId,
                webSocket: new WebSocket(environment.endpoints.organisationCompanyKnowledgeCenterWebsocket(orgId, conId)),
                isConnected: false,
            };
            this.webSocketParams.webSocket!.addEventListener('message', (event: MessageEvent) => {
                this.message.next(event.data);
            });
            this.webSocketParams.webSocket!.addEventListener('close', () => {
                this.cleanUpWebSocket();
            });
            this.webSocketParams.webSocket!.addEventListener('open', () => {
                this.webSocketParams.isConnected = true;
                let queueCopy = [...this.queue];
                for (let v of queueCopy) {
                    this.queue.pop();
                    this.webSocketParams.webSocket?.send(this.getMessage(v));
                }
            });
        }
        return this.message.asObservable();
    }

    sendMessage(value: string) {
        if (this.webSocketParams.isConnected && this.webSocketParams.webSocket) {
            this.webSocketParams.webSocket.send(this.getMessage(value));
        } else {
            this.queue.push(value);
        }
    }

    disconnect() {
        if (this.webSocketParams.webSocket) {
            this.webSocketParams.webSocket.close();
        }
        this.cleanUpWebSocket();
    }

    cleanUpWebSocket() {
        this.webSocketParams = { isConnected: false };
        this.queue = [];
        this.message.complete();
    }

    private getMessage(value: string) {
        return JSON.stringify({ message: value, token: `Bearer ${this.authService.getAccessToken()}` });
    }

    listRagFiles(orgId: number): Observable<RagFile[]> {
        return this.http.get<RagFile[]>(environment.endpoints.organisationKnowledgeCenterRagfileList(orgId)).pipe(
            map((ragfiles) =>
                ragfiles
                    .map((r) => new RagFile().deserialize(r))
                    .sort((r1, r2) => {
                        return r1.filename?.localeCompare(r2.filename ?? '') ?? 0;
                    }),
            ),
        );
    }

    createRagFile(orgId: number, file: File): Observable<RagFile> {
        const formData = new FormData();
        formData.append('file', file, file.name);
        return this.http
            .post<RagFile>(environment.endpoints.organisationKnowledgeCenterRagfileList(orgId), formData)
            .pipe(map((rf) => new RagFile().deserialize(rf)));
    }

    deleteRagFile(orgId: number, ragfileId: number): Observable<void> {
        return this.http.delete(environment.endpoints.organisationKnowledgeCenterRagfileDetail(orgId, ragfileId)).pipe(map(() => {}));
    }

    getRagFileStatus(orgId: number, ragfileId: number, ragfileStatusId: number): Observable<RagFileStatus> {
        return this.http
            .get<RagFileStatus>(environment.endpoints.organisationKnowledgeCenterRagfileStatusDetails(orgId, ragfileId, ragfileStatusId))
            .pipe(map((rfs) => new RagFileStatus().deserialize(rfs)));
    }

    createRagFileStatus(orgId: number, ragfileId: number): Observable<RagFileStatus> {
        return this.http
            .post<RagFileStatus>(environment.endpoints.organisationKnowledgeCenterRagfileStatusList(orgId, ragfileId), {})
            .pipe(map((rfs) => new RagFileStatus().deserialize(rfs)));
    }

    createRagFileStatusAndPollResult(orgId: number, ragfileId: number): Observable<RagFileStatus> {
        return this.createRagFileStatus(orgId, ragfileId).pipe(
            delay(LONG_DELAY_STATUS_FETCH_MS),
            mergeMap((newStatus: RagFileStatus) =>
                this.getRagFileStatus(orgId, ragfileId, newStatus.id).pipe(
                    tap((s) => {
                        if (s.status === PollingStatus.Init || s.status === PollingStatus.InProgress) {
                            throw Error(invalidErrorMessage);
                        }
                    }),
                    retry({
                        delay: (error) => {
                            if (error.message !== invalidErrorMessage) {
                                throw error;
                            } else {
                                return of(true).pipe(delay(LONG_DELAY_STATUS_FETCH_MS));
                            }
                        },
                    }),
                ),
            ),
        );
    }

    getSharePointSite(orgId: number, siteId: number): Observable<SharepointSite> {
        return this.http
            .get<SharepointSite>(environment.endpoints.organisationKnowledgeCenterSharePointSiteDetails(orgId, siteId))
            .pipe(map((rfs) => new SharepointSite().deserialize(rfs)));
    }

    createSharePointSiteAndPollResult(orgId: number, selectedSite: SharepointSite): Observable<SharepointSite> {
        return this.createSite(orgId, selectedSite).pipe(
            delay(LONG_DELAY_STATUS_FETCH_MS),
            mergeMap((newSite: SharepointSite) =>
                this.getSharePointSite(orgId, newSite.id!).pipe(
                    tap((s) => {
                        if (s.status === PollingStatus.Init || s.status === PollingStatus.InProgress) {
                            throw Error(invalidErrorMessage);
                        }
                    }),
                    retry({
                        delay: (error) => {
                            if (error.message !== invalidErrorMessage) {
                                throw error;
                            } else {
                                return of(true).pipe(delay(LONG_DELAY_STATUS_FETCH_MS));
                            }
                        },
                    }),
                ),
            ),
        );
    }

    getFollowedSites(orgId: number): Observable<SharepointSite[]> {
        return this.http
            .get<SharepointSite[]>(environment.endpoints.organisationKnowledgeCenterListSharepointSites(orgId))
            .pipe(map((rfs) => rfs.map((site) => new SharepointSite().deserialize(site))));
    }

    createSite(orgId: number, site: SharepointSite): Observable<SharepointSite> {
        return this.http.post<SharepointSite>(environment.endpoints.organisationKnowledgeCenterSelectedSite(orgId), site.serialize()).pipe(
            map((response) => {
                const updatedSite = new SharepointSite().deserialize(response);
                return updatedSite;
            }),
        );
    }

    deleteSelectedSite(orgId: number, siteId: number) {
        return this.http.delete<SharepointSite>(environment.endpoints.organisationKnowledgeCenterSharePointSiteDetails(orgId, siteId));
    }

    listPredefinedQuestions(orgId: number): Observable<PredefinedQuestion[]> {
        return this.http
            .get<PredefinedQuestion[]>(environment.endpoints.organisationKnowledgeCenterListPredefinedQuestions(orgId))
            .pipe(map((questions) => questions.map((p) => new PredefinedQuestion().deserialize(p))));
    }

    listTokenCounters(orgId: number, startDate?: Date, endDate?: Date): Observable<TokenCounter[]> {
        const params: Record<string, any> = {};
        if (startDate) params['start_date'] = startDate.toISOString().split('T')[0];
        if (endDate) params['end_date'] = endDate.toISOString().split('T')[0];

        return this.http
            .get<TokenCounter[]>(environment.endpoints.organisationKnowledgeCenterListTokenCounters(orgId), { params })
            .pipe(map((tokencounters) => tokencounters.map((t) => new TokenCounter().deserialize(t))));
    }

    listUsageTiers(orgId: number): Observable<AthenaUsageTier[]> {
        return this.http.get<AthenaUsageTier[]>(environment.endpoints.organisationKnowledgeCenterListAthenaUsageTiers(orgId)).pipe(
            map((usagetiers) => {
                return usagetiers.map((u) => new AthenaUsageTier().deserialize(u)).sort(AthenaUsageTier.Sort);
            }),
        );
    }
}
